import React from "react";
import MessageInbox from "./components/MessageInbox";
import ConversationThread from "./components/ConversationThread";
import { auth } from "../../firebaseConfig";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import { useUserData } from "../../hooks/useUsers";
import { Row, Col, Button, Drawer, Spin, Typography } from "antd";
import { MessageTwoTone, MenuOutlined, HeartOutlined } from "@ant-design/icons";
import { useAppSelector } from "../../hooks/redux-hooks";

const { Title, Text } = Typography;

const Messenger = () => {
  let navigate = useNavigate();
  const [user] = useAuthState(auth);
  const { loading, userData } = useUserData(user?.uid);
  const [visible, setVisible] = React.useState(false);
  const chatID = useAppSelector((state) => state.currentChat.chatID);

  React.useEffect(() => {
    if (loading) return;
    if (userData && !userData.profileCompleted) {
      navigate('/onboarding');
    } else {
      return
    }
  }, [user, userData, loading, navigate]);

  const showDrawer = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      paddingTop: '150px',
      paddingBottom: '20px',
      paddingLeft: '20px',
      paddingRight: '20px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <Spin spinning={loading} size="large">
          
          {/* Desktop Layout */}
          <div className="mobileHidden">
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              height: 'calc(100vh - 200px)',
              minHeight: '700px'
            }}>
              <Row style={{ height: '100%' }}>
                <Col 
                  xs={24} 
                  sm={8} 
                  md={8} 
                  lg={6}
                  style={{
                    borderRight: '1px solid rgba(0, 0, 0, 0.08)',
                    height: '100%'
                  }}
                >
                  <MessageInbox />
                </Col>
                <Col 
                  xs={24} 
                  sm={16} 
                  md={16} 
                  lg={18}
                  style={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {chatID ? (
                    <ConversationThread />
                  ) : (
                    <div style={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(145deg, #f8f9ff 0%, #ffffff 100%)'
                    }}>
                      <div style={{ textAlign: 'center', padding: '60px 40px' }}>
                        <HeartOutlined 
                          style={{ 
                            fontSize: '80px', 
                            marginBottom: '24px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                          }} 
                        />
                        <Title level={2} style={{ 
                          color: '#2c3e50', 
                          marginBottom: '16px',
                          fontWeight: '600'
                        }}>
                          Meaningful Connections
                        </Title>
                        <Text style={{ 
                          color: '#64748b', 
                          fontSize: '18px',
                          lineHeight: '1.6',
                          display: 'block',
                          marginBottom: '8px'
                        }}>
                          Select a conversation to start building
                        </Text>
                        <Text style={{ 
                          color: '#94a3b8', 
                          fontSize: '16px' 
                        }}>
                          a meaningful relationship through thoughtful messages
                        </Text>
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="mobileVisible">
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              height: 'calc(100vh - 200px)',
              minHeight: '600px'
            }}>
              {/* Mobile Header */}
              <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <Button 
                  type="primary"
                  onClick={showDrawer}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    borderRadius: '12px',
                    height: '44px',
                    width: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  icon={<MenuOutlined style={{ fontSize: '18px' }} />}
                />
                <Title level={4} style={{ 
                  margin: 0, 
                  color: 'white',
                  fontWeight: '600'
                }}>
                  Messages
                </Title>
              </div>

              <Drawer
                title={
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px' 
                  }}>
                    <MessageTwoTone style={{ fontSize: '24px' }} />
                    <span>Conversations</span>
                  </div>
                }
                placement="left"
                onClose={onClose}
                open={visible}
                width={320}
                styles={{
                  body: { padding: 0 },
                  header: { 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderBottom: 'none'
                  }
                }}
              >
                <MessageInbox />
              </Drawer>

              <div style={{ 
                height: 'calc(100% - 77px)',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {chatID ? (
                  <ConversationThread />
                ) : (
                  <div style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(145deg, #f8f9ff 0%, #ffffff 100%)'
                  }}>
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <HeartOutlined 
                        style={{ 
                          fontSize: '60px', 
                          marginBottom: '20px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }} 
                      />
                      <Title level={3} style={{ 
                        color: '#2c3e50', 
                        marginBottom: '12px' 
                      }}>
                        Meaningful Messages
                      </Title>
                      <Text style={{ 
                        color: '#64748b', 
                        fontSize: '16px' 
                      }}>
                        Tap the menu to select a conversation and start building meaningful connections
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

        </Spin>
      </div>
    </div>
  );
};

export default Messenger;